/**
 * SecretDayCard — locked itinerary day placeholder (Wave 26.21 Phase 2b).
 *
 * Restrained, cinematic. Never numerical "5d 4h 23m 14s" countdown.
 * Just a poetic hint + (if the advisor has scheduled a reveal) a calm
 * "Unlocks tonight at 10:00 PM" line.
 *
 * Props:
 *   moment: {
 *     day_number, date, label (the poetic hint),
 *     kind ('secret_reveal' | 'secret_day'),
 *     scheduled_reveal_at?: ISO,
 *   }
 *   tripId: for deep-link to itinerary
 */
import React from "react";
import { Link } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";

function formatRevealLine(iso) {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const now = new Date();
  const sameDay = at.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const isTomorrow = at.toDateString() === tomorrow.toDateString();
  const time = at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (sameDay) return `Unlocks tonight at ${time}`;
  if (isTomorrow) return `Unlocks tomorrow at ${time}`;
  const day = at.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  return `Unlocks ${day} at ${time}`;
}

export default function SecretDayCard({ moment, tripId }) {
  const reveal = moment.kind === "secret_reveal" ? formatRevealLine(moment.scheduled_reveal_at) : null;
  return (
    <Link
      to={`/portal/client/experiences/${tripId}/itinerary`}
      className="block group"
      data-testid={`secret-day-card-${moment.day_number}`}
    >
      <div className="relative overflow-hidden rounded-md bg-gradient-to-br from-[#1a1a1a] via-[#23211c] to-[#1a1a1a] border border-[hsl(var(--accent-gold))]/15 px-5 py-5 sm:px-6 sm:py-6">
        {/* Soft luminous corner — gold pinpoint, very restrained */}
        <div className="pointer-events-none absolute -top-12 -right-12 size-32 rounded-full bg-[hsl(var(--accent-gold))]/15 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="size-3 text-[hsl(var(--accent-gold))]/80" strokeWidth={1.5} />
            <span className="font-mono text-[10px] uppercase tracking-[0.26em] text-[hsl(var(--accent-gold))]/85">
              Day {moment.day_number}
              {moment.date ? ` · ${new Date(`${moment.date}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" })}` : ""}
            </span>
          </div>
          <p className="font-accent italic text-[18px] sm:text-[20px] leading-snug max-w-md text-white" style={{ opacity: 0.92 }}>
            {moment.label}
          </p>
          {reveal && (
            <p className="mt-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]/80">
              <Sparkles className="size-3" strokeWidth={1.5} />
              {reveal}
            </p>
          )}
          {!reveal && (
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
              Awaiting reveal
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}
